// Submit-button gate for the Order Trigger and Exit collapsible sections. Each
// helper returns the first blocking error message (for display above the submit
// button) or null when the section is inactive / not applicable / valid. The
// trigger reuses the full per-kind zod validator; the exit applies structural
// checks always and the TP-above / SL-below direction check only when a live
// reference price is available.
import { isExitActive } from '@/features/order/order-form/lib/exit-active'
import { resolveConditionTree } from '@/features/order/order-form/lib/resolve-condition-tree'
import { isTriggerActive } from '@/features/order/order-form/lib/trigger-active'
import { validateConditionTree } from '@/features/order/order-form/lib/trigger-condition-schema'
import type { OrderFormCollapsibleConfig } from '@/state/order'

function legValue(raw: string): number {
  return Number(raw.replace(/,/g, ''))
}

/**
 * First blocking error for the active Order Trigger, or null when the trigger is
 * disabled or fully valid. An armed-but-empty trigger reports the same prompt the
 * submit handler shows, so the button and the click path stay in lockstep.
 */
export function triggerValidationError(
  config: OrderFormCollapsibleConfig | null | undefined
): string | null {
  if (!isTriggerActive(config)) return null
  const root = resolveConditionTree(config)
  if (!root) return 'Configure a trigger condition'
  return validateConditionTree(root)
}

export interface ExitValidationOpts {
  /**
   * Whether the exit bracket can attach at all. False for TWAP / limit-chase
   * (algos can't carry bracket legs) and for multi-instrument orders (one TP/SL
   * price can't span a basket) — in those cases the section is informational and
   * never blocks submit.
   */
  applicable: boolean
  /** Live reference price, when known, for the direction check. */
  currentPrice?: number
  /**
   * Entry side of the order. The bracket fires on the opposite side, so the
   * valid TP/SL direction flips: a long takes profit above / stops below market,
   * a short takes profit below / stops above. Defaults to a long when unknown.
   */
  side?: 'buy' | 'sell'
}

/**
 * First blocking error for an armed Exit bracket, or null when inactive, not
 * applicable in the current mode, or valid. Structural checks (exactly one leg
 * is set and is a positive number) always apply; the direction check is
 * side-aware and only runs when a live price is available, so a momentarily
 * unknown price never flickers the submit button.
 */
export function exitValidationError(
  config: OrderFormCollapsibleConfig | null | undefined,
  opts: ExitValidationOpts
): string | null {
  if (!opts.applicable) return null
  if (!isExitActive(config)) return null

  const tp = (config?.priceAbove ?? '').trim()
  const sl = (config?.priceBelow ?? '').trim()
  if (!tp && !sl) return 'Set a take profit or stop loss price'
  // No backend OCO: only one leg may rest at a time, otherwise a filled leg
  // would leave the other dangling on the book.
  if (tp && sl) return 'Set only a take profit or a stop loss, not both'

  const isShort = opts.side === 'sell'
  if (tp) {
    const n = legValue(tp)
    if (!(n > 0)) return 'Take profit must be a number greater than 0'
    if (opts.currentPrice) {
      // Long: TP rests above market. Short: TP rests below market.
      if (!isShort && n < opts.currentPrice) {
        return `Take profit must be above ${opts.currentPrice.toLocaleString()}`
      }
      if (isShort && n > opts.currentPrice) {
        return `Take profit must be below ${opts.currentPrice.toLocaleString()}`
      }
    }
  }
  if (sl) {
    const n = legValue(sl)
    if (!(n > 0)) return 'Stop loss must be a number greater than 0'
    if (opts.currentPrice) {
      // Long: SL sits below market. Short: SL sits above market.
      if (!isShort && n > opts.currentPrice) {
        return `Stop loss must be below ${opts.currentPrice.toLocaleString()}`
      }
      if (isShort && n < opts.currentPrice) {
        return `Stop loss must be above ${opts.currentPrice.toLocaleString()}`
      }
    }
  }
  return null
}

/** First blocking error across the trigger then the exit, or null when both pass. */
export function collapsibleValidationError(
  config: OrderFormCollapsibleConfig | null | undefined,
  exitOpts: ExitValidationOpts
): string | null {
  return triggerValidationError(config) ?? exitValidationError(config, exitOpts)
}
